/**
 * queue/guestSession.tsx
 * Authorization for the PUBLIC surfaces (Guest QR self-join, Now Serving door
 * board) — which have no login. On a public surface the link carries the venue
 * + queue (`?org=<tenantId>&q=<slug>`); we exchange that at the Backbone for a
 * short-lived, narrowly-scoped guest token (queue:guest:join, queue:view) and
 * hold it in memory. The browser never holds a platform secret — the Backbone
 * is the only minter (see sk-backbone-api /v1/queue/guest-token).
 *
 * Screens read `authHeader` and pass it on their queueService calls. The token
 * is re-minted on demand (it's short-lived); the Backbone caches per org so
 * re-mint is cheap. Inert unless the URL actually carries org+slug, so the
 * authenticated admin surfaces (cookie-auth) are unaffected.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Queue_Guest_Token_Url } from '../api/endpoints';
import { handleApiError } from '../utils/errorHandler';

/** Public-safe queue info returned alongside the guest token. */
export interface GuestQueueInfo {
  id: string;
  name: string;
  product: string;
  acceptingEntries: boolean;
  selfJoinEnabled: boolean;
  selfRemovalEnabled: boolean;
  maxSelfJoinParty: number | null;
}

interface GuestSessionValue {
  /** True when the URL carries a public org+slug (i.e. this IS a public surface). */
  isPublic: boolean;
  queueId: string | null;
  queueInfo: GuestQueueInfo | null;
  loading: boolean;
  /** Authorization header to attach on public queueService calls, or null. */
  authHeader: () => Promise<Record<string, string> | null>;
  reload: () => void;
}

const GuestSessionContext = createContext<GuestSessionValue | null>(null);

/** Read org+slug from the URL (web). `?org=<tenantId>&q=<slug>`. */
function readLink(): { org: string; slug: string } | null {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search);
  const org = p.get('org') ?? '';
  const slug = p.get('q') ?? p.get('slug') ?? '';
  return org && slug ? { org, slug } : null;
}

export function GuestSessionProvider({ children }: { children: React.ReactNode }) {
  const link = readLink();
  const isPublic = !!link;
  const [queueId, setQueueId] = useState<string | null>(null);
  const [queueInfo, setQueueInfo] = useState<GuestQueueInfo | null>(null);
  const [loading, setLoading] = useState(isPublic);

  // Token lives in a REF (not just state) so authHeader is identity-stable: the
  // board polls now-serving every 4s and calls authHeader each time; if the
  // header callback changed identity when the token was set, the poll effect
  // would tear down + re-run + re-mint on a loop → backbone 429s. The ref keeps
  // authHeader stable, and `inflight` dedupes concurrent first-mints.
  const tokenRef = useRef<string | null>(null);
  // Epoch-seconds the cached token expires. The guest token is short-lived
  // (~15 min); without tracking this we'd hand out an expired token forever and
  // every call (join, verify, poll) would 401 with no re-mint. Re-mint when
  // within EXP_SKEW of expiry.
  const tokenExpRef = useRef<number>(0);
  const inflight = useRef<Promise<string | null> | null>(null);
  const org = link?.org;
  const slug = link?.slug;

  // Decode a JWT's `exp` (epoch seconds) without verifying — purely to know when
  // to proactively re-mint. Returns 0 if unreadable (forces a re-mint).
  const readExp = (jwt: string): number => {
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      return typeof payload.exp === 'number' ? payload.exp : 0;
    } catch { return 0; }
  };
  const EXP_SKEW = 30; // seconds of safety margin before true expiry

  const mint = useCallback(async (): Promise<string | null> => {
    if (!org || !slug) return null;
    if (inflight.current) return inflight.current; // single-flight: share one mint
    inflight.current = (async () => {
      try {
        const { data } = await axios.post<{ token: string; queue: GuestQueueInfo }>(
          Queue_Guest_Token_Url,
          { org, slug },
          { headers: { 'Content-Type': 'application/json' } },
        );
        tokenRef.current = data.token;
        tokenExpRef.current = readExp(data.token);
        setQueueId(data.queue.id);
        setQueueInfo(data.queue);
        return data.token;
      } finally {
        inflight.current = null;
      }
    })();
    return inflight.current;
  }, [org, slug]);

  const reload = useCallback(() => {
    if (!org || !slug) { setLoading(false); return; }
    setLoading(true);
    mint().catch((err) => handleApiError(err, 'Could not open this queue')).finally(() => setLoading(false));
  }, [mint, org, slug]);

  useEffect(() => { reload(); }, [reload]);

  // Bearer header for public calls. Reuses the cached token (ref); only mints
  // when there's none yet. Stable identity (deps don't include `token`), so it
  // never retriggers the caller's polling effect. A 401 mid-session → reload().
  const authHeader = useCallback(async (): Promise<Record<string, string> | null> => {
    if (!isPublic) return null;
    // Re-mint if we have no token OR the cached one is at/near expiry — a stale
    // token 401s every call (the old code cached it forever).
    const fresh = tokenRef.current && tokenExpRef.current > Date.now() / 1000 + EXP_SKEW;
    const t = fresh ? tokenRef.current : await mint();
    return t ? { Authorization: `Bearer ${t}` } : null;
  }, [isPublic, mint]);

  return (
    <GuestSessionContext.Provider value={{ isPublic, queueId, queueInfo, loading, authHeader, reload }}>
      {children}
    </GuestSessionContext.Provider>
  );
}

export function useGuestSession(): GuestSessionValue {
  const ctx = useContext(GuestSessionContext);
  if (!ctx) throw new Error('useGuestSession must be used within GuestSessionProvider');
  return ctx;
}
